// Products.js
import React, { useEffect, useState, useCallback, memo, forwardRef, useRef } from "react";
import "./Products.css";
import { UilSearch, UilEdit, UilTrashAlt } from "@iconscout/react-unicons";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ColorPreviewCanvas from "../../Components/ColorPreviewImage/ColorPreviewCanvas";
import { useSelector, useDispatch } from "react-redux";
import { emptyUser } from "../../Redux/slices/AuthSlice";
import { describeApiError } from "../../utils/apiError";

// ─── Config ──────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// The OVH bucket serves its own Access-Control-Allow-Origin: * on GET/HEAD,
// so images can be loaded directly — no need to round-trip through the backend.
const toProxiedUrl = (url) => url;

const formatPreorderDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ─── Axios instance ───────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// ─── Size helpers ─────────────────────────────────────────────────────────────
const adultSizesList   = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
const childSizesList   = ['4', '6', '8', '10', '12', '14', '16'];
const isAdultSize      = (s) => adultSizesList.includes(s);
const isChildrenSize   = (s) => childSizesList.includes(s);

const getProductSizeTypes = (product) => {
  const sizes = new Set();
  if (product.colorsAndSizes && typeof product.colorsAndSizes === 'object') {
    const firstColor = Object.keys(product.colorsAndSizes)[0];
    if (firstColor) Object.keys(product.colorsAndSizes[firstColor] || {}).forEach(s => sizes.add(s));
  }
  if (product.sizesOnly && typeof product.sizesOnly === 'object') {
    Object.keys(product.sizesOnly).forEach(s => sizes.add(s));
  }
  let hasAdult = false, hasChild = false;
  sizes.forEach(s => { if (isAdultSize(s)) hasAdult = true; if (isChildrenSize(s)) hasChild = true; });
  if (!hasAdult && !hasChild && product.sizeType) return [product.sizeType];
  const types = [];
  if (hasAdult) types.push('adult');
  if (hasChild) types.push('child');
  return types.length > 0 ? types : ['adult'];
};

const formatSizesWithTypes = (sizes) => {
  if (!sizes || Object.keys(sizes).length === 0) return <div className="no-sizes-message">—</div>;

  const adultOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const adult = {}, child = {};
  Object.entries(sizes).forEach(([s, q]) => {
    if (isAdultSize(s)) adult[s] = q;
    else if (isChildrenSize(s)) child[s] = q;
    else adult[s] = q;
  });

  const sortedAdult = Object.entries(adult).sort((a, b) => adultOrder.indexOf(a[0]) - adultOrder.indexOf(b[0]));
  const sortedChild = Object.entries(child).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

  return (
    <div className="sizes-compact-container">
      {sortedAdult.length > 0 && (
        <div className="size-group">
          <div className="size-group-header"><span className="sizes-title">Adultes</span></div>
          <div className="sizes-compact adult-sizes">
            {sortedAdult.map(([size, qty]) => (
              <span key={size} className={`size-badge ${qty > 0 ? 'in-stock' : 'out-of-stock'}`}
                title={`${size}: ${qty} pièces`}>
                <span className="size-label">{size}</span>
                <span className="size-qty">{qty}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {sortedChild.length > 0 && (
        <div className="size-group" style={{ marginTop: sortedAdult.length > 0 ? '12px' : '0' }}>
          <div className="size-group-header"><span className="sizes-title">Enfants</span></div>
          <div className="sizes-compact children-sizes">
            {sortedChild.map(([size, qty]) => (
              <span key={size} className={`size-badge ${qty > 0 ? 'in-stock' : 'out-of-stock'}`}
                title={`${size} ans: ${qty} pièces`}>
                <span className="size-label">{size} ans</span>
                <span className="size-qty">{qty}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronLeftIcon  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>;
const ChevronRightIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>;

// ─── ProductRow ───────────────────────────────────────────────────────────────
const ProductRow = memo(forwardRef(({ product, onDelete, isDeleting }, ref) => {
  const navigate = useNavigate();
  const [selectedColorName, setSelectedColorName] = useState(null);
  const [showAllColors, setShowAllColors]         = useState(false);
  const [imageError, setImageError]               = useState(false);
  const canvasRef = useRef(null);

  const isFinished      = product.isFinishedProduct === true; // ✅ Finished product flag
  const isPreorder      = product.isPreorder === true;
  const preorderLabel   = formatPreorderDate(product.preorderAvailableAt);
  const productSizeTypes = getProductSizeTypes(product);
  const hasBothTypes    = productSizeTypes.length === 2;

  const getRawImageUrl = useCallback(() => {
    if (!product.imgs?.length) return null;
    const img = product.imgs.find(item => item?.type === 'front') || product.imgs[0];
    for (const key of ['url', 'original', 'medium', 'thumbnail']) {
      const val = img?.[key];
      if (val?.url && typeof val.url === 'string') return val.url;
      if (val && typeof val === 'string' &&
          (val.startsWith('http://') || val.startsWith('https://') || val.startsWith('data:image'))) {
        return val;
      }
    }
    return null;
  }, [product.imgs]);

  const rawProductImage = getRawImageUrl();
  const productImage = rawProductImage ? toProxiedUrl(rawProductImage) : null;

  useEffect(() => {
    setImageError(false);
  }, [product._id]);

  const getAllColors = () => {
    if (isFinished) return [];
    if (product.colorsAndSizes && typeof product.colorsAndSizes === 'object' && !Array.isArray(product.colorsAndSizes)) {
      return Object.keys(product.colorsAndSizes);
    }
    if (Array.isArray(product.colorsAndSizes)) {
      return product.colorsAndSizes.map(c => c.color);
    }
    return [];
  };

  const getSizesForColor = (colorName) => {
    if (!colorName) return {};
    if (product.colorsAndSizes && typeof product.colorsAndSizes === 'object' && !Array.isArray(product.colorsAndSizes)) {
      return product.colorsAndSizes[colorName] || {};
    }
    if (Array.isArray(product.colorsAndSizes)) {
      return product.colorsAndSizes.find(c => c.color === colorName)?.sizes || {};
    }
    return {};
  };

  const colors           = getAllColors();
  const currentColorName = selectedColorName || colors[0] || null;
  const currentSizes     = isFinished ? (product.sizesOnly || {}) : getSizesForColor(currentColorName);
  const MAX_VISIBLE      = 5;
  const visibleColors    = colors.slice(0, MAX_VISIBLE);
  const hiddenColors     = colors.slice(MAX_VISIBLE);

  const getSizeTypeBadge = () => {
    if (hasBothTypes) return '👥 Adultes + Enfants';
    if (productSizeTypes.includes('child')) return '👶 Enfant';
    return '👤 Adulte';
  };

  return (
    <TableRow ref={ref} className="product-row" style={{ opacity: isDeleting ? 0.5 : 1 }}>
      <TableCell align="left" className="product-cell">
        <div className="product-display">
          <div className="product-image-container-large">
            {productImage && !imageError ? (
              isFinished ? (
                <img
                  src={productImage}
                  alt={product.title || product._id || 'Produit fini'}
                  loading="lazy"
                  decoding="async"
                  onError={() => setImageError(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
              ) : (
                <ColorPreviewCanvas
                  ref={canvasRef}
                  imageUrl={productImage}
                  color={currentColorName}
                  onError={() => setImageError(true)}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )
            ) : (
              <div className="image-fallback-large">
                {product.title?.charAt(0) || product.category?.charAt(0) || 'P'}
              </div>
            )}
          </div>
          <div className="product-info">
            <span className="product-name-extra-large">
              {product.title || product._id || 'Produit'}
            </span>
            <div className="product-badges" style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
              <span className={`product-size-type-badge ${hasBothTypes ? 'both-badge' : (productSizeTypes.includes('child') ? 'child-badge' : 'adult-badge')}`} style={{
                display: 'inline-block', fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
                background: hasBothTypes ? '#6c63ff' : (productSizeTypes.includes('child') ? '#28a745' : '#0B2A5C'),
                color: 'white',
              }}>
                {getSizeTypeBadge()}
              </span>
              {isFinished && (
                <span style={{
                  display: 'inline-block', fontSize: '10px', background: '#ff9800',
                  color: 'white', padding: '2px 8px', borderRadius: '20px',
                }}>
                  🏷️ Produit fini
                </span>
              )}
              {isPreorder && (
                <span className="availability-badge preorder">
                  Precommande
                </span>
              )}
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell align="left" className="supplier-cell">
        <span className="supplier-text">{product.fournisseur || 'N/A'}</span>
      </TableCell>

      <TableCell align="left" className="colors-sizes-cell">
        {formatSizesWithTypes(currentSizes)}

        {!isFinished && colors.length > 0 ? (
          <div className="colors-compact-container" style={{ marginTop: '12px' }}>
            <div className="colors-header">
              <span className="colors-title">Couleurs ({colors.length})</span>
            </div>
            <div className="colors-display">
              {visibleColors.map((colorName, idx) => (
                <button
                  key={idx}
                  className={`color-dot ${colorName === currentColorName ? 'selected' : ''}`}
                  style={{ backgroundColor: colorName }}
                  onClick={() => setSelectedColorName(colorName)}
                  title={colorName}
                />
              ))}
              {hiddenColors.length > 0 && (
                <div className="color-dropdown">
                  <button className="color-dropdown-btn" onClick={() => setShowAllColors(!showAllColors)}>
                    +{hiddenColors.length}
                  </button>
                  {showAllColors && (
                    <div className="color-dropdown-menu">
                      {hiddenColors.map((colorName, idx) => (
                        <button
                          key={idx}
                          className={`color-dropdown-item ${colorName === currentColorName ? 'selected' : ''}`}
                          style={{ backgroundColor: colorName }}
                          onClick={() => { setSelectedColorName(colorName); setShowAllColors(false); }}
                          title={colorName}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : !isFinished ? (
          <div className="no-sizes-message" style={{ marginTop: '10px' }}>Aucune couleur</div>
        ) : (
          <div className="no-sizes-message" style={{ marginTop: '10px', color: '#ff9800' }}>
            ✓ Produit fini - vente directe
          </div>
        )}
      </TableCell>

      <TableCell align="left" className="price-cell">
        <span className="price-text">€{product.purchase || '0'}</span>
      </TableCell>

      <TableCell align="left" className="availability-cell">
        <div className={`availability-pill ${isPreorder ? 'preorder' : 'standard'}`}>
          {isPreorder ? 'Precommande' : 'Catalogue'}
        </div>
        {isPreorder && (
          <div className="availability-date">
            Dispo {preorderLabel ? `le ${preorderLabel}` : 'bientot'}
          </div>
        )}
      </TableCell>

      <TableCell align="left" className="action-cell">
        <div className="action-buttons-group">
          {/* ✅ Only show "Nouveau modèle" for non-finished products */}
          {!isFinished && (
            <Link className="action-icon-btn btn-primary" to={`/add-design/${product._id}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              Nouveau modèle
            </Link>
          )}
          <button className="action-icon-btn btn-edit" onClick={() => navigate(`${product._id}`)} title="Modifier">
            <span className="btn-icon-inner"><UilEdit /></span>
          </button>
          <button
            className="action-icon-btn btn-delete"
            onClick={() => onDelete(product._id)}
            disabled={isDeleting}
            title="Supprimer"
          >
            <span className="btn-icon-inner"><UilTrashAlt /></span>
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}));

// ─── Products (main component) ────────────────────────────────────────────────
const Products = () => {
  const [products, setProducts]         = useState([]);
  const [filteredProducts, setFiltered] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [deletingIds, setDeletingIds]   = useState(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const itemsPerPage = 15;

  const { currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Sync auth token to axios after Redux rehydration
  useEffect(() => {
    const rawToken    = currentUser?.token || null;
    const refreshToken = currentUser?.refreshToken || null;

    if (rawToken) {
      const token = rawToken.startsWith("Bearer ") ? rawToken : `Bearer ${rawToken}`;
      api.defaults.headers.common["Authorization"] = token;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }

    if (refreshToken) {
      api.defaults.headers.common["x-refresh-token"] = refreshToken;
    } else {
      delete api.defaults.headers.common["x-refresh-token"];
    }
  }, [currentUser]);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/products', {
        params: { page: currentPage, limit: itemsPerPage },
      });
      const raw = res.data.products || [];
      setProducts(raw);
      setFiltered(raw);
      const pg = res.data.pagination;
      if (pg) {
        setTotalPages(pg.totalPages);
        setTotalItems(pg.totalItems);
      } else if (res.data.totalPages) {
        setTotalPages(res.data.totalPages);
        setTotalItems(res.data.totalProducts);
      }
    } catch (err) {
      console.error('Fetch products error:', err);
      const described = describeApiError(err, 'Erreur de chargement');
      setError(described);
      toast.error(`${described.title} - ${described.message}`, { position: 'top-right', autoClose: 6000 });
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    let nextProducts = products;

    if (q) {
      nextProducts = nextProducts.filter(p =>
        p._id?.toString().toLowerCase().includes(q) ||
        p.title?.toLowerCase().includes(q) ||
        p.fournisseur?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }

    if (availabilityFilter === 'preorder') {
      nextProducts = nextProducts.filter(p => p.isPreorder === true);
    } else if (availabilityFilter === 'standard') {
      nextProducts = nextProducts.filter(p => p.isPreorder !== true);
    }

    setFiltered(nextProducts);
  }, [search, products, availabilityFilter]);

  const preorderCount = products.filter(p => p.isPreorder === true).length;
  const standardCount = products.length - preorderCount;

  const handleDeleteProduct = async (id) => {
    if (!window.confirm(`Supprimer ce produit ?\n\nID: ${id}\n\nCette action est irréversible.`)) return;
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      await api.delete(`/products/${id}`);
      fetchProducts();
      window.dispatchEvent(new CustomEvent('productsChanged'));
      toast.success('Produit supprimé avec succès', { position: 'top-right', autoClose: 3000 });
    } catch (err) {
      console.error('Delete error:', err);
      let msg = 'Erreur lors de la suppression';
      if (err.response) {
        const { status, data } = err.response;
        if (status === 400) msg = data?.message || 'Suppression bloquée.';
        else if (status === 401) msg = 'Non autorisé. Veuillez vous reconnecter.';
        else if (status === 403) msg = "Vous n'avez pas les permissions nécessaires.";
        else if (status === 404) { msg = 'Produit non trouvé.'; fetchProducts(); }
        else if (status === 500) msg = 'Erreur serveur.';
        else msg = data?.error || data?.message || msg;
      } else if (err.request) {
        msg = 'Impossible de contacter le serveur.';
      }
      toast.error(msg, { position: 'top-right', autoClose: err.response?.status === 400 ? 8000 : 5000 });
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const goToPage = (n) => { setCurrentPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const goToPrev = () => { if (currentPage > 1) goToPage(currentPage - 1); };
  const goToNext = () => { if (currentPage < totalPages) goToPage(currentPage + 1); };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, '...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  // Signs out and goes to the login page - the fix when the server rejects the
  // stored token (expired or issued by another server).
  const handleReconnect = () => {
    dispatch(emptyUser());
    navigate('/login');
  };

  if (error) {
    return (
      <div className="error">
        <h3>{error.title}</h3>
        <p>{error.message}</p>
        {error.detail && (
          <p style={{ fontSize: '12px', opacity: 0.8, marginTop: '6px' }}>Détail technique : {error.detail}</p>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
          {error.isAuthError && (
            <button onClick={handleReconnect} style={{ padding: '8px 16px' }}>Se reconnecter</button>
          )}
          <button onClick={fetchProducts} style={{ padding: '8px 16px' }}>Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className='Products'>
      <ToastContainer />

      <div className="availability-filter-bar">
        <button
          type="button"
          className={`availability-filter-btn ${availabilityFilter === 'all' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('all')}
        >
          Tous ({products.length})
        </button>
        <button
          type="button"
          className={`availability-filter-btn preorder ${availabilityFilter === 'preorder' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('preorder')}
        >
          Precommandes ({preorderCount})
        </button>
        <button
          type="button"
          className={`availability-filter-btn ${availabilityFilter === 'standard' ? 'active' : ''}`}
          onClick={() => setAvailabilityFilter('standard')}
        >
          Catalogue ({standardCount})
        </button>
      </div>

      <div className="search-parent">
        <div className="search-large">
          <input
            className="search-text-large"
            placeholder="Rechercher un produit..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <UilSearch className="search-icon" />
        </div>
      </div>

      <div className="Table">
        <TableContainer component={Paper} className="table-container">
          <Table sx={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead>
              <TableRow className="table-header">
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>Produit</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>Fournisseur</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>Tailles et couleurs</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>Prix d'achat</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>B2B</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '16px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" className="no-products">
                    {availabilityFilter === 'preorder'
                      ? 'Aucun produit en precommande'
                      : search
                        ? 'Aucun produit trouvé pour cette recherche'
                        : 'Aucun produit disponible'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product, idx) => (
                  <ProductRow
                    key={product._id || idx}
                    product={product}
                    onDelete={handleDeleteProduct}
                    isDeleting={deletingIds.has(product._id)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="admin-pagination">
          <button className="pagination-btn prev-btn" onClick={goToPrev} disabled={currentPage === 1}>
            <ChevronLeftIcon /><span>Précédent</span>
          </button>
          <div className="pagination-numbers">
            {totalPages === 0 ? (
              <button className="pagination-number active">1</button>
            ) : (
              getPageNumbers().map((page, idx) =>
                page === '...' ? (
                  <span key={`dots-${idx}`} className="pagination-dots">...</span>
                ) : (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                )
              )
            )}
          </div>
          <button className="pagination-btn next-btn" onClick={goToNext} disabled={currentPage === totalPages || totalPages === 0}>
            <span>Suivant</span><ChevronRightIcon />
          </button>
        </div>

        <div className="pagination-info">
          Affichage {filteredProducts.length} sur {totalItems} produits
        </div>

        {loading && (
          <div className="loading-spinner-large">
            <div className="spinner-large"></div>
            <p>Chargement des produits...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
